class IntakerHelper {
    constructor(element) {
        this.maxSelectedProducts = 20;
        this.minDoseFromUpperLimitPercentage = 60;
        this.element = element;
        this.searchInput = this.getElement('search-input');
        this.searchResetButton = this.getElement('clear-search');
        this.submitButton = this.getElement('check-compatibility');
        this.loadingContainer = this.getElement('products-container');
        this.productList = this.getElement('product-list');
        this.selectedProducts = [];
        this.lastInputValue = '';
        this.abortController = new AbortController();
        this.extendingProductList = false;
        this.step = 'selection';
        this.productSelectionDisabled = false;
        this.domDelegateInstance = new window.domDelegate.Delegate(this.element);
        this.fetchOnInput = this.debounce(this.fetchProductList.bind(this), 325);
        this.attachListeners();
        const urlSearchParams = new URLSearchParams(location.search);
        if (urlSearchParams.has('selected-products')) {
            this.selectedProducts = decodeURIComponent(urlSearchParams.get('selected-products')).split(',');
            for (const child of this.productList.children) {
                const input = child.querySelector('input[name="selected-products-list"]');
                if (input)
                    input.checked = this.selectedProducts.includes(input.value);
            }
            this.displayProductSelection();
            if (this.selectedProducts.length >= 2) {
                this.fetchResults();
            }
        }
    }
    getElement(name, container) {
        const searchContainer = container || this.element;
        return searchContainer.querySelector(`[data-element="${name}"]`);
    }
    attachListeners() {
        this.searchInput.addEventListener('keydown', this.onSubmission.bind(this));
        this.searchInput.addEventListener('input', this.onInput.bind(this));
        this.searchResetButton.addEventListener('click', () => {
            if (this.searchInput.value === '')
                return;
            this.searchInput.value = '';
            this.searchInput.focus();
            this.searchInput.selectionStart = this.searchInput.selectionEnd = 100;
            this.toggleLoadingState(true);
            // Abort currently running requests since they are not needed anymore
            this.abortController.abort('Search query outdated');
            this.abortController = new AbortController();
            this.searchResetButton.classList.remove('none');
            this.fetchProductList('');
        });
        this.domDelegateInstance.on('change', 'input[name="selected-products-list"]', (e) => {
            const thisInput = e.target;
            if (thisInput.checked) {
                this.selectedProducts.push(thisInput.value);
                this.selectedProducts.sort();
            }
            else {
                this.selectedProducts = this.selectedProducts.filter(title => title !== thisInput.value);
            }
            this.displayProductSelection();
        });
        this.domDelegateInstance.on('click', '[data-element="remove-selected-product"]', (e) => {
            const productElement = e.target.closest('[data-element="selected-product"]');
            const productTitle = this.getElement('selected-product-title', productElement).innerText;
            this.selectedProducts = this.selectedProducts.filter(title => title !== productTitle);
            const correspondingInput = this.productList.querySelector(`input[name="selected-products-list"][value="${productTitle}"]`);
            if (correspondingInput) {
                correspondingInput.checked = false;
            }
            this.displayProductSelection();
        });
        this.domDelegateInstance.on('click', '[data-element="clear-product-selection"]', () => {
            this.selectedProducts = [];
            for (const input of this.productList.querySelectorAll('input[name="selected-products-list"]:checked')) {
                input.checked = false;
            }
            this.displayProductSelection();
        });
        const productsContainer = this.getElement('products-container');
        productsContainer.addEventListener('scroll', () => {
            if (this.extendingProductList)
                return;
            if (!this.productList.getAttribute('data-pagination'))
                return;
            const paginationData = JSON.parse(this.productList.getAttribute('data-pagination'));
            if (paginationData.current_page >= paginationData.pages)
                return;
            if (productsContainer.scrollTop + productsContainer.clientHeight < productsContainer.scrollHeight - 100)
                return;
            this.extendingProductList = true;
            this.fetchProductList('', paginationData.current_page + 1);
        }, { passive: true });
        this.submitButton.addEventListener('click', this.fetchResults.bind(this));
        const backButton = this.getElement('back-button');
        backButton.addEventListener('click', () => {
            this.setStep('selection');
            this.getElement('product-selection').scrollIntoView({ behavior: 'smooth' });
        });
        this.domDelegateInstance.on('change', 'select[data-element="variant-selector"]', (e) => {
            const selectElement = e.target;
            const selectedVariantId = selectElement.value;
            const ingredientsContainer = selectElement.closest('[data-element="ingredients"]').querySelector(`[data-ingredients-variant="${selectedVariantId}"]`);
            if (!ingredientsContainer)
                return;
            const displayedIngredientsContainer = selectElement.closest('[data-element="ingredients"]').querySelector('[data-ingredients-variant]:not(.none)');
            if (displayedIngredientsContainer)
                displayedIngredientsContainer.classList.add('none');
            ingredientsContainer.classList.remove('none');
        });
    }
    debounce(fn, delay) {
        const _this = this;
        let timer = null;
        return (...args) => {
            if (timer) {
                clearTimeout(timer);
            }
            timer = setTimeout(() => {
                fn.apply(_this, args);
            }, delay);
        };
    }
    setStep(step) {
        this.step = step;
        this.element.setAttribute('data-step', this.step);
    }
    displayProductSelection() {
        const selectionElement = this.getElement('selected-products');
        const template = this.getElement('selected-product-template', selectionElement);
        for (let i = template.parentElement.children.length - 1; i >= 0; i--) {
            const child = template.parentElement.children[i];
            if (child.getAttribute('data-element') === 'selected-product')
                child.remove();
        }
        for (const productTitle of this.selectedProducts) {
            const productElement = template.cloneNode(true);
            productElement.setAttribute('data-element', 'selected-product');
            this.getElement('selected-product-title', productElement).innerHTML = productTitle;
            productElement.classList.remove('none');
            template.parentElement.appendChild(productElement);
        }
        if (this.selectedProducts.length > 0) {
            this.getElement('selected-products-empty', selectionElement).classList.add('none');
            this.getElement('selected-products-not-empty', selectionElement).classList.remove('none');
        }
        else {
            this.getElement('selected-products-empty', selectionElement).classList.remove('none');
            this.getElement('selected-products-not-empty', selectionElement).classList.add('none');
        }
        this.getElement('selected-products-count', selectionElement).innerText = this.selectedProducts.length.toString();
        this.submitButton.disabled = this.selectedProducts.length < 2;
        this.productSelectionDisabled = this.selectedProducts.length >= this.maxSelectedProducts;
        const maximumReachedElement = this.getElement('selected-products-maximum', selectionElement);
        if (maximumReachedElement)
            maximumReachedElement.classList.toggle('none', !this.productSelectionDisabled);
        const productListInputs = this.productList.querySelectorAll('input[name="selected-products-list"]');
        for (const input of productListInputs) {
            input.disabled = this.productSelectionDisabled && !input.checked;
        }
    }
    toggleLoadingState(isLoading) {
        this.loadingContainer.classList.toggle('loading', isLoading);
    }
    /** Redirect to search page on submission in widget */
    onSubmission(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    }
    /** Actions on user input without fetching new results instantly */
    onInput(e) {
        const event = e;
        if (event.key === 'Enter') {
            return;
        }
        this.toggleLoadingState(true);
        // Abort currently running requests since they are not needed anymore
        this.abortController.abort('Search query outdated');
        this.abortController = new AbortController();
        const target = event.target;
        this.lastInputValue = target.value;
        if (this.lastInputValue !== '') {
            this.searchResetButton.classList.remove('none');
        }
        this.fetchOnInput(target.value);
    }
    /** Fetch search results for the given query and possibly active filters and sorting options */
    async fetchProductList(q, page) {
        try {
            const queryOptions = {
                method: 'GET',
                credentials: 'same-origin',
                signal: this.abortController.signal
            };
            const query = q ? `${window.routes.searchUrl}?section_id=intake-helper-products&type=product&options[unavailable_products]=show&q=${encodeURIComponent(q)}` : `${location.pathname}?section_id=intake-helper-products${page !== undefined ? `&page=${page}` : ''}`;
            const response = await fetch(query, queryOptions);
            const responseText = await response.text();
            const temp = document.createElement('div');
            temp.innerHTML = responseText;
            const tempProductList = this.getElement('product-list', temp);
            if (page === undefined) {
                this.productList.innerHTML = '';
            }
            else {
                this.getElement('product-list-loading', this.productList)?.remove();
            }
            if (tempProductList.hasAttribute('data-pagination')) {
                this.productList.setAttribute('data-pagination', tempProductList.getAttribute('data-pagination'));
            }
            this.productList.append(...tempProductList.children);
            for (const child of this.productList.children) {
                const input = child.querySelector('input[name="selected-products-list"]');
                if (!input)
                    continue;
                input.checked = this.selectedProducts.includes(input.value);
                input.disabled = this.productSelectionDisabled && !input.checked;
            }
            this.toggleLoadingState(false);
        }
        catch (error) {
            if (error.name === 'AbortError') {
                return;
            }
            console.error('Error fetching product list:', error);
            this.toggleLoadingState(false);
        }
        finally {
            this.extendingProductList = false;
        }
    }
    async fetchResults() {
        try {
            const url = new URL(location.href);
            url.searchParams.set('selected-products', this.selectedProducts.join(','));
            history.replaceState(null, '', url.toString());
            const compatibilityResultsContainer = this.getElement('compatibility-results');
            const intakeInfoResultsContainer = this.getElement('intake-info-results');
            compatibilityResultsContainer.innerHTML = '';
            compatibilityResultsContainer.classList.add('loading');
            intakeInfoResultsContainer.innerHTML = '';
            intakeInfoResultsContainer.classList.add('loading');
            this.submitButton.classList.add('loading');
            const response = await fetch(`${window.routes.searchUrl}?section_id=intake-helper-results&type=product&options[unavailable_products]=show&options[prefix]=none&q=${encodeURIComponent(this.selectedProducts.map(product => `title:"${product}"`).join(' OR '))}`, {
                method: 'GET',
            });
            const responseText = await response.text();
            const temp = document.createElement('div');
            temp.innerHTML = responseText;
            const newCompatibilityResultsContainer = this.getElement('compatibility-results', temp);
            for (let i = newCompatibilityResultsContainer.children.length - 1; i >= 0; i--) {
                const child = newCompatibilityResultsContainer.children[i];
                compatibilityResultsContainer.insertAdjacentElement('afterbegin', child);
            }
            this.calculateCompatibility();
            compatibilityResultsContainer.classList.remove('loading');
            const newIntakeInfoResultsContainer = this.getElement('intake-info-results', temp);
            for (let i = newIntakeInfoResultsContainer.children.length - 1; i >= 0; i--) {
                const child = newIntakeInfoResultsContainer.children[i];
                intakeInfoResultsContainer.insertAdjacentElement('afterbegin', child);
            }
            intakeInfoResultsContainer.classList.remove('loading');
            this.setStep('results');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        catch (error) {
            console.error('Error fetching results:', error);
        }
        finally {
            this.submitButton.classList.remove('loading');
        }
    }
    /**
     * Look up the dose of a nutrient in the nutrition facts data. The dose is converted to µg if necessary. If the nutrient is not found or the dose cannot be parsed, 0 is returned.
     * @returns Value of the nutrient dose always in µg
     */
    getNutrientDose(nutritionFacts, nutrientName) {
        const stringsToRemove = ['- davon '];
        const nutrientNameLower = nutrientName.toLowerCase();
        for (const row of nutritionFacts) {
            if (row.length < 2)
                continue;
            let name = row[0].toLowerCase();
            for (const str of stringsToRemove) {
                name = name.replace(str, '');
            }
            if (name === nutrientNameLower) {
                const dose = this.getDoseInMicroGrams(row[1]);
                return dose ?? 0;
            }
        }
        return 0;
    }
    getDoseInMicroGrams(dose) {
        const doseParts = dose.split(' ');
        if (doseParts.length !== 2)
            return null;
        let doseValue = parseFloat(doseParts[0].replace(',', '.'));
        if (isNaN(doseValue))
            return null;
        const doseUnit = doseParts[1];
        if (doseUnit === 'mg') {
            doseValue *= 1000;
        }
        else if (doseUnit === 'g') {
            doseValue *= 1_000_000;
        }
        else if (doseUnit !== 'µg' && doseUnit !== 'mcg') {
            return null;
        }
        return doseValue;
    }
    calculateCompatibility() {
        const data = JSON.parse(this.getElement('compatibility-data').innerText);
        for (const product of data.products) {
            for (const compareProduct of data.products) {
                if (product.id === compareProduct.id)
                    continue;
                let iconType = 'allowed';
                const iconElement = this.element.querySelector(`[data-element="compatibility-icon"][data-product-id="${product.id}"][data-compare-product-id="${compareProduct.id}"]`);
                if (!iconElement)
                    continue;
                let nutrientDoses = {};
                if (product.nutrition_facts) {
                    nutrientDoses = product.nutrients.filter(nutrient => nutrient.daily_upper_limit).reduce((dict, nutrient) => {
                        const dose = this.getNutrientDose(product.nutrition_facts.data, nutrient.name);
                        const upperLimit = this.getDoseInMicroGrams(nutrient.daily_upper_limit);
                        if (upperLimit !== null && dose / upperLimit * 100 >= this.minDoseFromUpperLimitPercentage) {
                            dict[nutrient.id] = {
                                doses: [dose],
                                upperLimit: upperLimit
                            };
                        }
                        return dict;
                    }, {});
                    if (compareProduct.nutrition_facts) {
                        for (const nutrient of compareProduct.nutrients) {
                            const existingNutrientDose = nutrientDoses[nutrient.id];
                            if (existingNutrientDose == undefined)
                                continue;
                            const dose = this.getNutrientDose(compareProduct.nutrition_facts.data, nutrient.name);
                            if (dose / existingNutrientDose.upperLimit * 100 >= this.minDoseFromUpperLimitPercentage)
                                existingNutrientDose.doses.push(dose);
                        }
                    }
                }
                if (Object.values(nutrientDoses).some(nutrientDose => nutrientDose.doses.filter(dose => dose > 0).length >= 2 && nutrientDose.doses.reduce((a, b) => a + b, 0) > nutrientDose.upperLimit)) {
                    iconType = 'illegal';
                }
                else if (product.nutrients.some(nutrient => compareProduct.nutrients.some(compareNutrient => nutrient.inhibiting_combinations.some(inhibitingCombination => inhibitingCombination.id === compareNutrient.id)))) {
                    iconType = 'restricted';
                }
                iconElement.innerHTML = data.icons[iconType];
            }
        }
    }
}
(() => {
    const intakeHelperElement = document.querySelector('.IntakeHelper');
    if (intakeHelperElement) {
        new IntakerHelper(intakeHelperElement);
    }
})();
export {};
